// Catalog.ID Client — Which keys this device holds (selective key import)
// ============================================================================
// Whitepaper §3.4.7, implemented by device-setup-restructure §7. A member
// decides, PER DEVICE, which of their keys the device keeps a copy of. The
// three Core keys are not part of that decision; everything else is.
//
// The decision is LOCAL. The server never learns it: there is no wire field
// and no schema column, by design — a node that knew which keys a device holds
// would know where to look for them. It lives beside the PIN length in
// localStorage, is swept with the device (`clearDeviceStorage`), and is read
// and written only through this module.
//
// Decisions are per key ID, never per generation (§1 item 11): a rotated
// generation of a chosen key imports at the next reconcile without asking
// again, and an id the member never decided about keeps the pre-feature
// behaviour — it counts as missing, the unlock routes to supersecret, and the
// picker shows it pre-ticked and marked new.

/** The per-device manifest as stored. `chosen` and `declined` hold key IDS —
 *  `main_signing_key`, `efs_encrypt`, or a family glob such as
 *  {@link AGENT_SIGNING_KEY_FAMILY} — never indexes and never generations. */
export interface DeviceKeyManifest {
    v: 1;
    /** Ids the member ticked. */
    chosen: string[];
    /** Ids the member unticked. An id here is neither stored on this device nor
     *  reported missing by the reconcile. */
    declined: string[];
}

/** The localStorage key holding {@link DeviceKeyManifest} for an account.
 *
 *  Deliberately NOT of the `{cid}_key_{id}@{n}` shape that
 *  `listEnrolledIdentities` reads: a manifest is a preference, not key
 *  material, and a browser holding one and nothing else is not enrolled. */
export function deviceKeyManifestStorageKey(cid: string): string {
    return `${cid}_device_keys`;
}

/** The keys every device that stores keys at all must hold (§1 item 10).
 *
 *  Not a member choice and not a refactor away: a device without
 *  `main_signing_key` cannot sign the step-up that changes its own mode; one
 *  without `attr_encrypt` breaks a delegate outright, because every
 *  organization key reaches them wrapped to it; one without `derive_root`
 *  cannot open a module blob, mint a payment token, sync the XRPL vault or
 *  sign a CPR ceremony — and every one of those is a NON-INTERACTIVE call that
 *  must never stop to ask for a supersecret. Changing this list is a decision
 *  (device-setup-restructure §12, §13 item 5). */
export const MANDATORY_DEVICE_KEY_IDS: readonly string[] = [
    'main_signing_key',
    'attr_encrypt',
    'derive_root',
];

/** One toggle for every agent's signing key. Agent keys are minted per agent
 *  (`agent:{cid}:agent_signing_key`), so the catalogue cannot list them by id;
 *  the `*` stands for the agent's cid. */
export const AGENT_SIGNING_KEY_FAMILY = 'agent:*:agent_signing_key';

/** The picker's groups, in display order. `Other` is not a catalogue group:
 *  it is where the picker shows a bag entry whose id no catalogue row knows
 *  (§7.2), by its raw id and optional. */
export type DeviceKeyGroup =
    | 'Core' | 'Messaging' | 'Roots' | 'Money & authority' | 'Files' | 'Agents' | 'Other';

/** One row of the picker. Exactly one of `keyId` / `family` is set. */
export interface DeviceKeyCatalogueRow {
    /** The exact bag key id this row governs. */
    keyId?: string;
    /** The family glob this row governs as one toggle (see
     *  {@link AGENT_SIGNING_KEY_FAMILY}); `*` matches one non-empty segment. */
    family?: string;
    /** What the member reads. */
    label: string;
    group: DeviceKeyGroup;
    /** One sentence saying what the device loses by declining it. */
    description: string;
    /** Ticked and disabled — see {@link MANDATORY_DEVICE_KEY_IDS}. */
    mandatory: boolean;
}

/**
 * The picker's rows, in display order (device-setup-restructure §7.2).
 *
 * Completeness against the client key vocabulary
 * (`crypto/keybag.ts` `REQUIRED_ALGORITHM_BY_KEY_ID`) is a TEST, not a
 * convention: a key id added there with no row here would be an undecided key
 * the member is never offered, which the reconcile then reports missing on
 * every unlock. See `device-key-selection.test.ts`.
 *
 * Not rows, and why:
 *  - `recovery_kit`   — never reaches a device at all (`isDeviceStorableKeyId`,
 *                       registration-restructure §16.5).
 *  - `bitcash_wallet` — no longer a personal bag entry (cid-encrypted-storage
 *                       §7.2); an ORGANIZATION's wallet key reaches a delegate
 *                       as a wrap under `attr_encrypt`, never as a device entry.
 *  - `xrpl_data_key`  — deleted by cid-encrypted-storage M6 and treated as
 *                       already absent here.
 */
export const DEVICE_KEY_CATALOGUE: readonly DeviceKeyCatalogueRow[] = [
    {
        keyId: 'main_signing_key', label: 'Signing key (approvals)', group: 'Core', mandatory: true,
        description: 'Signs every approval you give, including changes to this device.',
    },
    {
        keyId: 'attr_encrypt', label: 'Attribute key (claims, org keys)', group: 'Core', mandatory: true,
        description: 'Opens your claims and every organization key shared with you.',
    },
    {
        keyId: 'derive_root', label: 'Derivation root (module keys, blobs, registry authority)',
        group: 'Core', mandatory: true,
        description: 'The root every module key, stored document and payment token is derived from.',
    },
    {
        keyId: 'msg_encrypt', label: 'Messaging key', group: 'Messaging', mandatory: false,
        description: 'Opens messages addressed to your identity.',
    },
    {
        keyId: 'chat_encrypt', label: 'Chat key', group: 'Messaging', mandatory: false,
        description: 'Opens chat sent to your identity.',
    },
    {
        keyId: 'mail_encrypt', label: 'Mail key', group: 'Messaging', mandatory: false,
        description: 'Opens mail sent to your identity.',
    },
    {
        keyId: 'chat_sign', label: 'Chat root', group: 'Roots', mandatory: false,
        description: 'Issues this device\'s chat certificates.',
    },
    {
        keyId: 'mail_sign', label: 'Mail root', group: 'Roots', mandatory: false,
        description: 'Issues this device\'s mail certificates.',
    },
    {
        keyId: 'xrpl_master', label: 'XRPL master key', group: 'Money & authority', mandatory: false,
        description: 'Your XRPL account\'s master key. It is also on your printed sheet.',
    },
    {
        keyId: 'efs_encrypt', label: 'File storage key', group: 'Files', mandatory: false,
        description: 'Opens files stored for you in encrypted file storage.',
    },
    {
        family: AGENT_SIGNING_KEY_FAMILY, label: 'Agent signing keys', group: 'Agents', mandatory: false,
        description: 'Signs on behalf of the agents you run. One choice covers all of them.',
    },
];

/** True when `keyId` is a member of the family glob `family`, whose single `*`
 *  stands for one NON-EMPTY segment: `agent:*:agent_signing_key` matches
 *  `agent:khu3758iop:agent_signing_key` and neither the bare suffix nor
 *  `agent::agent_signing_key`. */
export function matchesDeviceKeyFamily(family: string, keyId: string): boolean {
    const star = family.indexOf('*');
    if (star === -1) return family === keyId;
    const prefix = family.slice(0, star);
    const suffix = family.slice(star + 1);
    return keyId.length > prefix.length + suffix.length
        && keyId.startsWith(prefix)
        && keyId.endsWith(suffix);
}

/** The catalogue row governing `keyId`, or null when no row knows it — the
 *  picker's "Other" case (§7.2). */
export function catalogueRowForKeyId(keyId: string): DeviceKeyCatalogueRow | null {
    for (const row of DEVICE_KEY_CATALOGUE) {
        if (row.keyId !== undefined && row.keyId === keyId) return row;
        if (row.family !== undefined && matchesDeviceKeyFamily(row.family, keyId)) return row;
    }
    return null;
}

function listed(list: readonly string[], keyId: string): boolean {
    for (const entry of list) {
        if (entry === keyId) return true;
        if (entry.includes('*') && matchesDeviceKeyFamily(entry, keyId)) return true;
    }
    return false;
}

/**
 * Does this device hold `keyId`?
 *
 * Mandatory → true, whatever the manifest says. Declined (by id or by family)
 * → false. Chosen → true. Undecided → TRUE: an id nobody has decided about
 * behaves exactly as it did before this feature existed, which is what keeps
 * the 2026-09-15 stale-copy guarantee intact for every key a future release
 * adds (§1 item 11).
 *
 * `manifest` null means there is none — every key is chosen, which is the
 * right answer for a device set up before the picker shipped (§7.1).
 */
export function isDeviceKeyChosen(manifest: DeviceKeyManifest | null, keyId: string): boolean {
    if (MANDATORY_DEVICE_KEY_IDS.includes(keyId)) return true;
    if (!manifest) return true;
    if (listed(manifest.declined, keyId)) return false;
    return true;
}

/** Everything the catalogue offers, chosen; nothing declined. What device setup
 *  writes when the member made no choice, and the starting point of the picker.
 *
 *  Explicit ids rather than an empty manifest: "chosen" and "undecided" behave
 *  the same for storage but differ in the picker, which marks an undecided id
 *  NEW. A row added by a later release should be new; one the member has
 *  already seen ticked should not. */
export function allChosenDeviceKeyManifest(): DeviceKeyManifest {
    return {
        v: 1,
        chosen: DEVICE_KEY_CATALOGUE.map(r => r.keyId ?? r.family!),
        declined: [],
    };
}

/** This device's manifest for an account, or null when it has none (→ every key
 *  is chosen). Malformed or future-versioned storage reads as null: the safe
 *  failure is "holds more than it must", never "holds less". */
export function readDeviceKeyManifest(cid: string): DeviceKeyManifest | null {
    const raw = localStorage.getItem(deviceKeyManifestStorageKey(cid));
    if (raw === null) return null;
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null) return null;
    const m = parsed as Partial<DeviceKeyManifest>;
    if (m.v !== 1) return null;
    if (!isStringArray(m.chosen) || !isStringArray(m.declined)) return null;
    return { v: 1, chosen: m.chosen, declined: m.declined };
}

function isStringArray(v: unknown): v is string[] {
    return Array.isArray(v) && v.every(x => typeof x === 'string');
}

/** Write this device's manifest for an account.
 *
 *  Normalizes on the way in: both lists are de-duplicated and no mandatory id
 *  is ever recorded as declined, so a stored manifest can be read literally by
 *  a later reader without re-applying the rule. {@link isDeviceKeyChosen}
 *  enforces the same thing on the way out, for a manifest written by hand or
 *  by an older build. */
export function writeDeviceKeyManifest(cid: string, manifest: DeviceKeyManifest): void {
    const value: DeviceKeyManifest = {
        v: 1,
        chosen: unique(manifest.chosen),
        declined: unique(manifest.declined).filter(id => !MANDATORY_DEVICE_KEY_IDS.includes(id)),
    };
    localStorage.setItem(deviceKeyManifestStorageKey(cid), JSON.stringify(value));
}

function unique(list: readonly string[]): string[] {
    return Array.from(new Set(list));
}

/** Forget this device's manifest for an account. Swept by `clearDeviceStorage`
 *  alongside the keys it describes. */
export function clearDeviceKeyManifest(cid: string): void {
    localStorage.removeItem(deviceKeyManifestStorageKey(cid));
}
